import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import { Dataset, listDatasets } from "@/lib/api/datasets";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  pollingAllowed,
  useResumeRefresh,
} from "@/components/hooks/activityGate";
import { ContextMenu } from "@/components/common/ContextMenu";
import { LoadingState } from "@/components/common/LoadingState";
import { SearchBar } from "@/components/common/SearchBar";
import { useCatalogCreateMenu } from "@/components/main/hooks/useCatalogCreateMenu";
import { SectionRule } from "components/common/SectionRule";
import { TabToggle, TabToggleOption } from "@/components/common/TabToggle";
import { DatasetRow } from "@/components/main/datasets/DatasetRow";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { ListEmptyShell } from "@/components/main/overview/ListEmptyShell";
import { CreateDatasetDialog } from "@/components/main/datasets/CreateDatasetDialog";
import { ManageOrganizationsDialog } from "@/components/main/datasets/ManageOrganizationsDialog";
import { Button } from "@/components/common/Button";
import { useAuth } from "@/contexts/AuthProvider";
import { VersionChip } from "@/components/main/utils/common/VersionChip";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useOnlineStatus } from "@/components/hooks/useOnlineStatus";
import {
  ColumnBottomSpacer,
  ColumnTopSpacer,
  LIST_MAX_HEIGHT,
} from "@/components/main/pages/pageColumn";

/// Auto-refresh cadence for the dataset list — mirrors the models list polling
/// so a dataset created elsewhere (e.g. the mcdp CLI) appears without a reload.
const POLL_INTERVAL_MS = 30_000;

type DatasetVisibility = "all" | "public" | "private";

const VISIBILITY_OPTIONS: readonly TabToggleOption<DatasetVisibility>[] = [
  { value: "all", label: "All" },
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

/// Warm across navigations so re-opening the page shows the last list
/// immediately and refetches in the background, instead of flashing the
/// full-screen "Loading datasets…" loader every time (matches the Teams /
/// Connections stores' SWR behaviour). `null` = never loaded this session.
let datasetsCache: Dataset[] | null = null;

/// Main-app Datasets page. Mirrors the Overview page shell: a centered column
/// with a fixed filter bar up top and a scrolling list whose section headers
/// stick. A dataset never widens what a viewer can see — shared members are
/// already filtered to the caller's discoverable set server-side.
export const DatasetsPage: React.FC = () => {
  const [datasets, setDatasets] = useState<Dataset[]>(
    () => datasetsCache ?? [],
  );
  const [loading, setLoading] = useState(() => datasetsCache === null);
  const [filter, setFilter] = useState("");
  const [visibility, setVisibility] = useState<DatasetVisibility>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [createCatalog, setCreateCatalog] = useState(false);
  const catalogMenu = useCatalogCreateMenu();
  const { canManagePublicCatalog } = useAuth();
  const [orgsOpen, setOrgsOpen] = useState(false);
  // Bumped on every registry mutation so open creator pickers re-read it.
  const [registryVersion, setRegistryVersion] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link target from a table's Datasets indicator (`?dataset={id}`). Read
  // once so the matching row mounts expanded; then drop the param so re-renders
  // don't keep re-triggering it.
  const [deepLinkId] = useState(() => searchParams.get("dataset"));
  // Page-owned so expanding one row collapses any other.
  const [openId, setOpenId] = useState<string | null>(deepLinkId);

  // D19: the listing is org-scoped, so a context switch is a new list — not a
  // filter over the one already rendered.
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  const refetch = useCallback(async () => {
    try {
      setDatasets(await listDatasets());
    } catch {
      showNotificationGlobal("Failed to load datasets", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch, activeOrgId]);

  // Silent background refresh: reuse the list fetch without the full-page
  // loading swap, and swallow errors (the initial `refetch` surfaces failures).
  const silentRefresh = useCallback(() => {
    listDatasets()
      .then(setDatasets)
      .catch(() => {});
  }, []);

  // Gated ambient polling, mirroring the models list.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (navigator.onLine && pollingAllowed()) silentRefresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [silentRefresh]);

  useResumeRefresh(
    useCallback(() => {
      if (navigator.onLine) silentRefresh();
    }, [silentRefresh]),
  );

  // Immediate refetch when connectivity is restored.
  const isOnline = useOnlineStatus();
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;
    if (isOnline && !wasOnline) silentRefresh();
  }, [isOnline, silentRefresh]);

  // Keep the module cache warm once loaded so the next open skips the loader.
  useEffect(() => {
    if (!loading) datasetsCache = datasets;
  }, [datasets, loading]);

  // Consume the deep-link param once (the seed was captured on first render).
  useEffect(() => {
    if (!deepLinkId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("dataset");
        return next;
      },
      { replace: true },
    );
  }, [deepLinkId, setSearchParams]);

  const openCreate = useCallback((catalog: boolean) => {
    setCreateCatalog(catalog);
    setCreateOpen(true);
  }, []);

  // Create reports `is_owner` from the anchor tuple, the list from `owner_id` —
  // they disagree for a catalog dataset, so refetch rather than leave the new row
  // in the wrong section until the next poll.
  const onCreated = useCallback(
    (dataset: Dataset) => {
      setDatasets((prev) => [dataset, ...prev]);
      if (dataset.is_catalog) silentRefresh();
    },
    [silentRefresh],
  );

  const onDeleted = useCallback((id: string) => {
    setDatasets((prev) => prev.filter((d) => d.id !== id));
    setOpenId((cur) => (cur === id ? null : cur));
  }, []);

  const onUpdated = useCallback((id: string, description: string | null) => {
    setDatasets((prev) =>
      prev.map((d) => (d.id === id ? { ...d, description } : d)),
    );
  }, []);

  const onRenamed = useCallback((id: string, name: string) => {
    setDatasets((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)));
  }, []);

  const setRowExpanded = useCallback((id: string, expanded: boolean) => {
    setOpenId((cur) => (expanded ? id : cur === id ? null : cur));
  }, []);

  // The text filter only ever searches the active visibility set.
  const inScope = useMemo(
    () =>
      visibility === "all"
        ? datasets
        : datasets.filter((d) => d.is_public === (visibility === "public")),
    [datasets, visibility],
  );
  const q = filter.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? inScope.filter(
            (d) =>
              d.name.toLowerCase().includes(q) ||
              d.labels.some((l) => l.name.toLowerCase().includes(q)),
          )
        : inScope,
    [inScope, q],
  );
  const owned = useMemo(() => filtered.filter((d) => d.is_owner), [filtered]);
  const shared = useMemo(() => filtered.filter((d) => !d.is_owner), [filtered]);
  const sharedTotal = inScope.filter((d) => !d.is_owner).length;
  const setNoun = (n: number) =>
    `${visibility === "all" ? "" : `${visibility} `}${n === 1 ? "dataset" : "datasets"}`;

  // Measured while every dataset shows, then held as the list's floor so a
  // narrower scope or filter never moves the bar above or the count below.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [listFloor, setListFloor] = useState(0);
  useLayoutEffect(() => {
    if (visibility === "all" && !q) {
      setListFloor(listRef.current?.clientHeight ?? 0);
    }
  }, [visibility, q, datasets, openId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoadingState size="large" isLoading label="Loading datasets..." />
      </div>
    );
  }

  const isEmpty = datasets.length === 0;

  return (
    <div className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6">
      <ColumnTopSpacer />
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "45rem",
          paddingTop: "2.5rem",
          paddingBottom: "2.5rem",
        }}
      >
        {isEmpty ? (
          <ListEmptyShell
            icon={<LayersOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Datasets"
            hint="Create a dataset to group related tables you can share."
            countLabel="0 datasets"
            addLabel="New dataset"
            onAdd={() => openCreate(false)}
            addMenuTriggerProps={catalogMenu.triggerProps}
            search={{
              value: filter,
              onChange: setFilter,
              placeholder: "Filter datasets…",
            }}
          />
        ) : (
          <>
            {/* Fixed: the filter bar stays put while the list below scrolls. */}
            <div className="shrink-0 mb-6">
              <SearchBar
                value={filter}
                onChange={setFilter}
                placeholder="Filter datasets…"
                size="lg"
                trailingAdornment={
                  <TabToggle<DatasetVisibility>
                    value={visibility}
                    onChange={setVisibility}
                    ariaLabel="Dataset visibility"
                    options={VISIBILITY_OPTIONS}
                  />
                }
              />
            </div>

            <div
              ref={listRef}
              className="overflow-y-auto"
              style={{
                maxHeight: LIST_MAX_HEIGHT,
                minHeight: listFloor || undefined,
              }}
            >
              <div key={visibility} className="list-scope-enter">
                {owned.length > 0 && (
                  <div className="mb-8">
                    <div className="sticky top-0 z-20 bg-bg overflow-hidden">
                      <SectionRule
                        inset
                        icon={<LayersOutlinedIcon sx={{ fontSize: 14 }} />}
                        label="Datasets"
                      />
                    </div>
                    {owned.map((d) => (
                      <DatasetRow
                        key={d.id}
                        dataset={d}
                        onDeleted={onDeleted}
                        onUpdated={onUpdated}
                        onRenamed={onRenamed}
                        expanded={openId === d.id}
                        onExpandedChange={(exp) => setRowExpanded(d.id, exp)}
                        registryVersion={registryVersion}
                      />
                    ))}
                  </div>
                )}

                {shared.length > 0 && (
                  <div className="mb-8">
                    <div className="sticky top-0 z-20 bg-bg overflow-hidden">
                      <SectionRule
                        inset
                        icon={<PeopleOutlineIcon sx={{ fontSize: 14 }} />}
                        label="Shared with you"
                      />
                    </div>
                    {shared.map((d) => (
                      <DatasetRow
                        key={d.id}
                        dataset={d}
                        onDeleted={onDeleted}
                        onUpdated={onUpdated}
                        onRenamed={onRenamed}
                        expanded={openId === d.id}
                        onExpandedChange={(exp) => setRowExpanded(d.id, exp)}
                        registryVersion={registryVersion}
                      />
                    ))}
                  </div>
                )}

                {filtered.length === 0 && (
                  <div className="text-center py-12 text-small text-fg-muted italic">
                    {q
                      ? `No ${setNoun(0)} match “${filter.trim()}”.`
                      : `No ${setNoun(0)}.`}
                  </div>
                )}
              </div>
            </div>

            {/* Pinned below the scroll: the count + New dataset pill stay
                visible while the list scrolls, and the pulse ring is never
                clipped by the scroll container's overflow. */}
            <div className="shrink-0 flex items-center mt-6">
              <span
                className="font-mono text-fg-muted"
                style={{
                  fontSize: "var(--font-size-x-small)",
                  letterSpacing: "0.04em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {[
                  `${inScope.length} ${setNoun(inScope.length)}`,
                  ...(visibility === "all" && sharedTotal > 0
                    ? [`${sharedTotal} shared with you`]
                    : []),
                ].join(" · ")}
              </span>
              <span className="ml-auto flex items-center gap-2">
                {canManagePublicCatalog && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setOrgsOpen(true)}
                  >
                    <CorporateFareOutlinedIcon className="icon-xs" />
                    Manage organisations
                  </Button>
                )}
                <MetaAddButton
                  label="New dataset"
                  onClick={() => openCreate(false)}
                  menuTriggerProps={catalogMenu.triggerProps}
                />
              </span>
            </div>
          </>
        )}
      </div>

      <ColumnBottomSpacer />
      <div className="absolute bottom-4 right-4 z-10">
        <VersionChip />
      </div>

      <ContextMenu
        ariaLabel="Create a dataset"
        position={catalogMenu.position}
        onClose={catalogMenu.close}
        items={[
          {
            label: "New dataset",
            icon: <AddOutlinedIcon className="icon-xs" />,
            onClick: () => openCreate(false),
          },
          {
            label: "New catalog dataset",
            icon: <PublicOutlinedIcon className="icon-xs" />,
            onClick: () => openCreate(true),
          },
        ]}
      />

      <ManageOrganizationsDialog
        open={orgsOpen}
        onClose={() => setOrgsOpen(false)}
        onChanged={() => setRegistryVersion((v) => v + 1)}
      />

      <CreateDatasetDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={onCreated}
        catalog={createCatalog}
      />
    </div>
  );
};
